<script lang="ts">
  import PrivacyToggle from '../lib/ui/PrivacyToggle.svelte';
  import { navigate, currentPath } from '../lib/router.svelte';
  import { clearToken, parseTs, DISCOVERY_PATH, type AdminUser } from '../lib/admin';
  import { activeCommunity, onCommunityChange } from '../lib/community.svelte';
  import { nav, toggleCollapsed } from '../lib/nav.svelte';
  import { todos, ensureTodoCounts } from '../lib/todos.svelte';

  let {
    me,
    mobile = false,
    onNavigate,
  }: { me: AdminUser | null; mobile?: boolean; onNavigate?: () => void } = $props();

  // The mobile drawer always shows full labels; only the desktop rail collapses.
  const collapsed = $derived(!mobile && nav.collapsed);
  const path = $derived(currentPath());

  type Leaf = { kind: 'leaf'; id: string; label: string; icon: string; to: string; match: (p: string) => boolean };
  type Section = { kind: 'section'; id: string; label: string; children: Leaf[] };
  type Node = Leaf | Section;

  const BASE_TREE: Node[] = [
    { kind: 'section', id: 'mission-control', label: 'Mission Control', children: [
      { kind: 'leaf', id: 'overview', label: 'Overview', icon: 'pulse', to: '/admin/overview',
        match: (p) => p === '/admin' || p.startsWith('/admin/overview') },
      { kind: 'leaf', id: 'inbox', label: 'Posts & Comments', icon: 'inbox', to: '/admin/moderation',
        match: (p) => p.startsWith('/admin/moderation') },
      { kind: 'leaf', id: 'pipelines', label: 'Pipelines', icon: 'columns', to: '/admin/pipelines',
        match: (p) => p.startsWith('/admin/pipelines') },
      { kind: 'leaf', id: 'automations', label: 'Automations', icon: 'send', to: '/admin/automations',
        match: (p) => p.startsWith('/admin/automations') },
    ] },
    { kind: 'section', id: 'insights', label: 'Insights', children: [
      // Where a Skool listing ranks and what to target next — and where signup carries on,
      // which is why it is the one page an account without a community can open.
      { kind: 'leaf', id: 'discovery', label: 'Discovery', icon: 'search', to: DISCOVERY_PATH,
        match: (p) => p.startsWith(DISCOVERY_PATH) },
      { kind: 'leaf', id: 'insights-contributors', label: 'Contributors', icon: 'users', to: '/admin/insights/contributors',
        match: (p) => p.startsWith('/admin/insights/contributors') },
      { kind: 'leaf', id: 'insights-engagement', label: 'Engagement', icon: 'trend', to: '/admin/insights/engagement',
        match: (p) => p.startsWith('/admin/insights/engagement') },
      // Replaced `Questions & gaps`, which went with the clustering pipeline. The old
      // path still routes, and redirects here.
      { kind: 'leaf', id: 'insights-digest', label: 'Weekly digest', icon: 'calendar', to: '/admin/insights/digest',
        match: (p) => p.startsWith('/admin/insights/digest') || p.startsWith('/admin/insights/questions') },
      { kind: 'leaf', id: 'members', label: 'Members', icon: 'user', to: '/admin/community/members',
        match: (p) => p.startsWith('/admin/community/members') },
      { kind: 'leaf', id: 'timeline', label: 'Timeline', icon: 'feed', to: '/admin/community/timeline',
        match: (p) => p.startsWith('/admin/community/timeline') || (p.startsWith('/admin/community') && !p.startsWith('/admin/community/members')) },
    ] },
    { kind: 'section', id: 'sideguide-ai', label: 'Sideguide AI', children: [
      { kind: 'leaf', id: 'usage', label: 'Usage', icon: 'spark', to: '/admin/insights/ai',
        match: (p) => p.startsWith('/admin/insights/ai') },
      { kind: 'leaf', id: 'conversations', label: 'Conversations', icon: 'message-circle', to: '/admin/conversations',
        match: (p) => p.startsWith('/admin/conversations') },
      { kind: 'leaf', id: 'indexed', label: 'Indexed Content', icon: 'layers', to: '/admin/content',
        match: (p) => p.startsWith('/admin/content') },
    ] },
  ];

  // Superadmins also get a dedicated Super admin section (pipeline health + the
  // invites/accounts console); creators never see it.
  const tree = $derived<Node[]>(
    me?.role === 'superadmin'
      ? [
          ...BASE_TREE,
          { kind: 'section', id: 'superadmin', label: 'Super admin', children: [
            { kind: 'leaf', id: 'health', label: 'Pipeline health', icon: 'pulse',
              to: '/admin/health', match: (p) => p.startsWith('/admin/health') },
            { kind: 'leaf', id: 'accounts', label: 'Accounts', icon: 'users',
              to: '/admin/accounts', match: (p) => p.startsWith('/admin/accounts') },
            { kind: 'leaf', id: 'audit-leads', label: 'Audit leads', icon: 'users',
              to: '/admin/audit-leads', match: (p) => p.startsWith('/admin/audit-leads') },
            { kind: 'leaf', id: 'invites', label: 'Invites', icon: 'tag',
              to: '/admin/invites', match: (p) => p.startsWith('/admin/invites') },
            { kind: 'leaf', id: 'affiliates', label: 'Affiliates', icon: 'gift',
              to: '/admin/affiliates', match: (p) => p.startsWith('/admin/affiliates') },
            // DM inboxes is intentionally not listed here — /admin/dm-inboxes is
            // still routed and reachable by URL, just hidden from the nav.
          ] },
        ]
      : BASE_TREE,
  );

  const prefsActive = $derived(path.startsWith('/admin/preferences'));

  // A creator who holds no community yet has nothing for the other pages to show, and each
  // of them would only send them back to Discovery. Say so on the item rather than bounce.
  const noCommunity = $derived(me?.role === 'creator' && me.skool_ids.length === 0);
  const lockedLeaf = (leaf: Leaf): boolean => noCommunity && leaf.id !== 'discovery';

  // Trial pill follows the ACTIVE community's plan (billing is per-skool now).
  const activeSkool = $derived(activeCommunity());

  // The "Todos" badge counts for the selected community.
  //
  // This used to fire FOUR requests here, on every navigation, forever, to produce
  // four integers (doc 02 W2). They are now the `badges` block of the one
  // `GET /v1/me` the page issues anyway, so a navigation costs nothing and only a
  // genuine community switch reaches the network — `ensureTodoCounts` decides.
  //
  // Still gated on `community.loaded`: the badges are chrome, and the id they must
  // agree with is the one the server resolved, which lands in the same round trip.
  onCommunityChange((id) => void ensureTodoCounts(id));

  // Badge count for a leaf: maps to a single Mission Control queue. Others: 0 (hidden).
  function badgeFor(id: string): number {
    if (id === 'inbox') return todos.inbox;
    return 0;
  }

  function go(to: string): void {
    navigate(to);
    onNavigate?.();
  }

  function logout(): void {
    clearToken();
    navigate('/admin/login');
    onNavigate?.();
  }

  const padLeaf = (depth: number) => `padding-left: ${0.75 + depth * 0.85}rem`;
</script>

{#snippet icon(name: string)}
  <svg class="h-[18px] w-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    {#if name === 'pulse'}<path d="M3 12h4l2 6 4-14 2 8h6" />
    {:else if name === 'users'}<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    {:else if name === 'message-square'}<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    {:else if name === 'feed'}<rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" />
    {:else if name === 'user'}<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    {:else if name === 'spark'}<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
    {:else if name === 'calendar'}<rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" />
    {:else if name === 'message-circle'}<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
    {:else if name === 'folder'}<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    {:else if name === 'layers'}<path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
    {:else if name === 'sliders'}<path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3M14 2v4M8 10v4M16 18v4" />
    {:else if name === 'shield'}<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    {:else if name === 'checklist'}<path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    {:else if name === 'inbox'}<path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    {:else if name === 'help'}<circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
    {:else if name === 'trend'}<polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    {:else if name === 'tag'}<path d="M20.59 13.41 11 3H4v7l9.59 9.59a2 2 0 0 0 2.82 0l4.18-4.18a2 2 0 0 0 0-2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
    {:else if name === 'refresh'}<polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    {:else if name === 'board'}<rect x="3" y="3" width="5" height="18" rx="1" /><rect x="10" y="3" width="5" height="12" rx="1" /><rect x="17" y="3" width="5" height="15" rx="1" />
    {:else if name === 'columns'}<rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
    {:else if name === 'search'}<circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    {:else if name === 'send'}<line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    {:else if name === 'gift'}<polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    {/if}
  </svg>
{/snippet}

{#snippet badge(count: number)}
  {#if count > 0}
    <span
      class="ml-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[0.65rem] font-semibold leading-none text-primary-content"
    >{count > 99 ? '99+' : count}</span>
  {/if}
{/snippet}

{#snippet leafRow(leaf: Leaf, depth: number)}
  {@const active = leaf.match(path)}
  {@const count = badgeFor(leaf.id)}
  {@const locked = lockedLeaf(leaf)}
  <button
    onclick={() => go(leaf.to)}
    disabled={locked}
    title={locked ? 'Available once your community is imported' : undefined}
    aria-current={active ? 'page' : undefined}
    style={padLeaf(depth)}
    class="flex w-full items-center gap-2.5 rounded-r-field rail py-1.5 pr-2 text-sm transition-colors
      {active
        ? 'rail-on bg-primary/10 font-medium text-primary'
        : locked
          ? 'cursor-not-allowed text-base-content/35'
          : 'text-base-content/75 hover:bg-base-200/50 hover:text-base-content'}"
  >
    {@render icon(leaf.icon)}
    <span class="truncate">{leaf.label}</span>
    {@render badge(count)}
  </button>
{/snippet}

{#snippet sectionBlock(section: Section)}
  <div class="pt-4">
    <div class="eyebrow px-3 pb-1 text-base-content/62">{section.label}</div>
    <div class="space-y-0.5">
      {#each section.children as child (child.id)}
        {@render leafRow(child, 0)}
      {/each}
    </div>
  </div>
{/snippet}

{#snippet railIcon(leaf: Leaf)}
  {@const active = leaf.match(path)}
  {@const count = badgeFor(leaf.id)}
  {@const locked = lockedLeaf(leaf)}
  <button
    onclick={() => go(leaf.to)}
    disabled={locked}
    title={locked ? `${leaf.label} — available once your community is imported` : count > 0 ? `${leaf.label} (${count})` : leaf.label}
    aria-label={count > 0 ? `${leaf.label}, ${count} pending` : leaf.label}
    aria-current={active ? 'page' : undefined}
    class="relative flex w-full items-center justify-center rounded-field rail py-2 transition-colors
      {active ? 'rail-on bg-primary/10 text-primary' : locked ? 'cursor-not-allowed text-base-content/35' : 'text-base-content/72 hover:bg-base-200/50 hover:text-base-content'}"
  >
    {@render icon(leaf.icon)}
    {#if count > 0}
      <span
        class="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.6rem] font-semibold leading-none text-primary-content"
      >{count > 9 ? '9+' : count}</span>
    {/if}
  </button>
{/snippet}

<div
  class="flex h-full shrink-0 flex-col bg-base-100/95 transition-[width] duration-150
    {collapsed ? 'w-14' : mobile ? 'w-72' : 'w-60'}"
>
  <!-- Brand. Height comes from `--app-header-h` (app.css), the SAME variable the
       content column's community header uses — the two sit side by side and their
       bottom borders read as one line across the viewport, so a number that lives in
       only one of them is a number that will drift. -->
  <div class="flex h-[var(--app-header-h)] items-center border-b hair px-3 {collapsed ? 'justify-center' : ''}">
    <button onclick={() => go('/admin/overview')} class="flex min-w-0 items-center gap-2" title="Sideguide admin">
      <img src="/icon-128.png" alt="" class="h-6 w-6 shrink-0 rounded-md" />
      {#if !collapsed}
        <span class="truncate font-display text-sm font-bold tracking-tight">Sideguide</span>
        <span class="eyebrow rounded border hair px-1.5 py-px">admin</span>
      {/if}
    </button>
    <!-- The demo redaction lives HERE, not in the shell's community header beside the
         trial and roster chips, for one reason: that header only renders when a
         community is selected, and a superadmin holds none — so the control would be
         missing on exactly the fleet-wide pages (Accounts, Pipeline health) that show
         every owner's email. This block is always mounted.

         The collapsed rail has no room for it, which is why `app.css` also draws a
         primary hairline across the top of the viewport whenever the mode is on: the
         "it is engaged" signal must not depend on a chrome state the user can toggle
         away, or on the sidebar being on screen at all. -->
    {#if !collapsed && me?.role === 'superadmin'}
      <div class="ml-auto pl-2"><PrivacyToggle /></div>
    {/if}
  </div>

  <!-- The community SELECTOR moved to the shell's header, where the community it names
       already is; keeping a second copy of the switcher here would be two controls for
       one piece of state. What stays is the self-serve "add another" action, which is
       an account-level thing and belongs with the account's own navigation. -->
  {#if !collapsed && me?.role === 'creator' && !noCommunity}
    <div class="space-y-2 border-b hair px-3 py-2">
      {#if me?.role === 'creator'}
        <!-- Self-serve: add another community (billing is per-community). -->
        <button
          onclick={() => go(`${DISCOVERY_PATH}?add=1`)}
          class="flex w-full items-center gap-1.5 rounded-field px-2 py-1 text-xs text-base-content/65 transition-colors hover:bg-base-200/50 hover:text-base-content"
        >
          <span class="text-sm leading-none">+</span> Add community
        </button>
      {/if}
    </div>
  {/if}

  <!-- Nav -->
  {#if collapsed}
    <nav aria-label="Admin" class="flex-1 space-y-1 overflow-y-auto px-2 py-3">
      {#each tree as node (node.id)}
        {#if node.kind === 'leaf'}
          {@render railIcon(node)}
        {:else}
          <div class="my-1 border-t hair"></div>
          {#each node.children as child (child.id)}
            {@render railIcon(child)}
          {/each}
        {/if}
      {/each}
    </nav>
  {:else}
    <nav aria-label="Admin" class="flex-1 overflow-y-auto px-2 py-3">
      {#each tree as node (node.id)}
        {#if node.kind === 'leaf'}
          {@render leafRow(node, 0)}
        {:else}
          {@render sectionBlock(node)}
        {/if}
      {/each}
    </nav>
  {/if}

  <!-- Preferences: dedicated bottom item, separate from the scrollable nav -->
  <div class="mt-auto border-t hair px-2 py-2">
    <button
      onclick={() => go('/admin/preferences')}
      aria-current={prefsActive ? 'page' : undefined}
      title="Preferences"
      aria-label="Preferences"
      class="flex w-full items-center gap-2.5 rounded-field py-1.5 pr-2 text-sm transition-colors
        {collapsed ? 'justify-center' : 'pl-3'}
        {prefsActive
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-base-content/75 hover:bg-base-200/50 hover:text-base-content'}"
    >
      {@render icon('sliders')}
      {#if !collapsed}<span class="truncate">Preferences</span>{/if}
    </button>
  </div>

  <!-- Footer -->
  <div class="border-t hair p-2">
    {#if !collapsed && me}
      {#if activeSkool?.plan_status === 'trial' && activeSkool.trial_ends_at}
        <!-- parseTs, never Date.parse: v1 emits a '+00:00' offset and the legacy
             route emits a bare naive instant, and only parseTs reads both as UTC. -->
        {@const daysLeft = Math.max(0, Math.ceil((parseTs(activeSkool.trial_ends_at).getTime() - Date.now()) / 86400000))}
        <div class="mx-2 mb-1.5 flex items-center gap-1.5 rounded-field bg-primary/10 px-2 py-1 text-[0.7rem] font-medium text-primary">
          <span class="inline-block h-1.5 w-1.5 rounded-full bg-primary"></span>
          Trial · {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left
        </div>
      {/if}
      <div class="px-2 pb-1.5 leading-tight">
        <div class="redact truncate font-mono text-xs text-base-content/70">{me.email}</div>
        <div class="eyebrow text-primary/80">{me.role}</div>
      </div>
    {/if}
    <div class="flex items-center gap-1 {collapsed ? 'flex-col' : ''}">
      <button
        onclick={logout}
        class="btn btn-ghost btn-sm flex-1 justify-start font-normal text-base-content/70 {collapsed ? 'btn-square flex-none' : ''}"
        title="Sign out"
        aria-label="Sign out"
      >
        <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        {#if !collapsed}<span>Sign out</span>{/if}
      </button>
      {#if !mobile}
        <button
          onclick={toggleCollapsed}
          class="btn btn-ghost btn-sm btn-square text-base-content/68"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            {#if collapsed}<polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" />
            {:else}<polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" />{/if}
          </svg>
        </button>
      {/if}
    </div>
  </div>
</div>
